import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError, parseApiError, getErrorMessage } from '../../utils/errorUtils';
import './Users.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';

const MIN_PASSWORD_LENGTH = 8;

const ViewUser = forwardRef(({ userId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onUserCreated }, ref) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [user, setUser] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [userSkills, setUserSkills] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedProficiency, setSelectedProficiency] = useState('BASIC');
  const [selectedCertDate, setSelectedCertDate] = useState(localToday());

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    fetchRoles();
    fetchDepartments();
    fetchAvailableSkills();
    if (userId && !createMode) {
      fetchUser();
      fetchUserSkills();
    } else if (createMode) {
      // Initialize with default values for new user
      const newUser = {
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        is_active: true,
        role: 'USER',
        role_id: 3,  // Default to User role
        role_ids: [3],
        primary_role_id: 3,
        department_id: null
      };
      setUser(newUser);
      setEditData(newUser);
    }
  }, [userId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          // In view mode, go back to list
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchDepartments = async () => {
    try {
      const response = await apiFetch(API_CONFIG.ENDPOINTS.DEPARTMENTS, {
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/roles/'));
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUserSkills = async () => {
    if (!userId) return;
    try {
      const response = await apiFetch(getApiUrl(`/api/skills/users/${userId}/skills`));
      if (response.ok) {
        const data = await response.json();
        setUserSkills(data.user_skills || []);
      }
    } catch (error) {
      console.error('Error fetching user skills:', error);
    }
  };

  const fetchAvailableSkills = async () => {
    try {
      const response = await apiFetch(API_CONFIG.ENDPOINTS.SKILLS + '?limit=1000');
      if (response.ok) {
        const data = await response.json();
        setAvailableSkills(data.skills || []);
      }
    } catch (error) {
      console.error('Error fetching available skills:', error);
    }
  };

  const handleAddUserSkill = async (skillId, proficiencyLevel, certificationDate) => {
    try {
      const response = await apiFetch(getApiUrl('/api/skills/users/skills'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          skill_id: skillId,
          proficiency_level: proficiencyLevel,
          certification_date: certificationDate || localToday()
        })
      });

      if (response.ok) {
        showSuccess(t('users.view.skills.addSkillSuccess'));
        fetchUserSkills();
      } else {
        await showApiError(response, showError, tCommon, t('users.view.skills.addSkillFailed'));
      }
    } catch (error) {
      console.error('Error adding skill:', error);
      showError(t('users.view.skills.addSkillFailed'), tCommon('errors.INTERNAL_SERVER_ERROR'));
    }
  };

  const handleRemoveUserSkill = async (userSkillId) => {
    if (!window.confirm(t('users.view.skills.removeConfirm'))) return;

    try {
      const response = await apiFetch(getApiUrl(`/api/skills/users/skills/${userSkillId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('users.view.skills.removeSuccess'));
        fetchUserSkills();
      } else {
        await showApiError(response, showError, tCommon, t('users.view.skills.addSkillFailed'));
      }
    } catch (error) {
      console.error('Error removing skill:', error);
      showError(t('users.view.skills.addSkillFailed'), tCommon('errors.INTERNAL_SERVER_ERROR'));
    }
  };

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/users/${userId}`));
      if (response.ok) {
        const data = await response.json();
        // Use data as-is from API
        // If role_id is missing, try to infer from role ENUM
        let roleId = data.role_id;
        if (!roleId && data.role) {
          const roleMap = { 'ADMIN': 1, 'MANAGER': 2, 'USER': 3, 'READONLY': 4, 'SALES': 5, 'SUPERVISOR': 6, 'OPERATOR': 7 };
          roleId = roleMap[data.role] || null;
        }
        // Roles (#285): every role of the user, primary first
        const roleIds = data.role_ids?.length ? data.role_ids : (roleId ? [roleId] : []);
        const userData = {
          ...data,
          role: data.role || 'USER',
          role_id: roleId,
          role_ids: roleIds,
          primary_role_id: roleId || roleIds[0] || null,
          department_id: data.department_id || null
        };
        setUser(userData);
        setEditData(userData);
      } else {
        setError(t('users.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setError(t('users.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };


  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Same rule as the backend and change-password: at least 8 characters (#352)
    if ((createMode || editData.password) && (editData.password || '').length < MIN_PASSWORD_LENGTH) {
      const message = t('users.view.validation.passwordMinLength', { min: MIN_PASSWORD_LENGTH });
      setError(message);
      showError(
        createMode ? t('users.view.messages.createFailed') : t('users.view.messages.updateFailed'),
        message
      );
      return;
    }
    try {
      // Prepare data for API - only include database fields
      const saveData = {
        username: editData.username,
        email: editData.email,
        first_name: editData.first_name,
        last_name: editData.last_name,
        is_active: editData.is_active,
        role_ids: editData.role_ids || [],
        primary_role_id: editData.primary_role_id,
        department_id: editData.department_id
      };

      // Add password if provided
      if (editData.password) {
        saveData.password = editData.password;
      }


      const url = createMode
        ? API_CONFIG.ENDPOINTS.USERS
        : getApiUrl(`/api/users/${userId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        showSuccess(
          createMode ? t('users.view.messages.createdTitle') : t('users.view.messages.updatedTitle'),
          createMode ? t('users.view.messages.createdSuccess') : t('users.view.messages.updatedSuccess')
        );

        if (createMode && onUserCreated) {
          onUserCreated(result.user_id || result.id);
        } else {
          // Refetch to get updated data
          await fetchUser();
          if (onSave) onSave(editData);
        }
      } else {
        const errorData = await response.json();
        const parsed = parseApiError(errorData);
        const errorMessage = getErrorMessage(parsed, tCommon);
        setError(errorMessage);
        showError(
          createMode ? t('users.view.messages.createFailed') : t('users.view.messages.updateFailed'),
          errorMessage
        );
      }
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setError(errorMessage);
      showError(
        createMode ? t('users.view.messages.createFailed') : t('users.view.messages.updateFailed'),
        errorMessage
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(user);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('users.view.messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/users/${userId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('users.view.messages.deletedTitle'), t('users.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        const parsed = await showApiError(response, showError, tCommon, t('users.view.messages.deleteFailed'));
        setError(getErrorMessage(parsed, tCommon));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setError(errorMessage);
      showError(t('users.view.messages.deleteFailed'), errorMessage);
    }
  };

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('users.view.sections.basicInfo')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('users.view.fields.username')} *</label>
            <input
              type="text"
              value={editData.username || ''}
              onChange={(e) => handleFieldChange('username', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.email')} *</label>
            <input
              type="email"
              value={editData.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.firstName')}</label>
            <input
              type="text"
              value={editData.first_name || ''}
              onChange={(e) => handleFieldChange('first_name', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.lastName')}</label>
            <input
              type="text"
              value={editData.last_name || ''}
              onChange={(e) => handleFieldChange('last_name', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.department')}</label>
            <select
              value={editData.department_id || ''}
              onChange={(e) => handleFieldChange('department_id', e.target.value ? parseInt(e.target.value) : null)}
              disabled={!editMode && !createMode}
            >
              <option value="" disabled hidden>{t('users.view.placeholders.selectDepartment')}</option>
              {departments.map(dept => (
                <option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.password')} {createMode && '*'}</label>
            <input
              type="password"
              value={editData.password || ''}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={createMode
                ? t('users.view.placeholders.passwordMin', { min: MIN_PASSWORD_LENGTH })
                : t('users.view.placeholders.passwordHint')}
              required={createMode}
              minLength={MIN_PASSWORD_LENGTH}
            />
          </div>
        </div>
      </div>

    </div>
  );

  // Roles (#285): several roles per user; permissions are the union, the primary role picks the
  // home screen. READONLY cannot be combined with other roles.
  const roleCodeOf = (roleId) => roles.find(r => r.role_id === roleId)?.role_code;
  const selectedRoleIds = editData.role_ids || [];
  const selectedRoleCodes = selectedRoleIds.map(roleCodeOf).filter(Boolean);

  const setUserRoles = (roleIds, primaryId) => {
    const primary = roleIds.includes(primaryId) ? primaryId : (roleIds[0] || null);
    setEditData(prev => ({
      ...prev,
      role_ids: roleIds,
      primary_role_id: primary,
      role_id: primary,
      role: roleCodeOf(primary) || prev.role
    }));
    setHasChanges(true);
  };

  const toggleRole = (roleId) => {
    if (selectedRoleIds.includes(roleId)) {
      setUserRoles(selectedRoleIds.filter(id => id !== roleId), editData.primary_role_id);
      return;
    }
    // READONLY replaces every other role, and any other role replaces READONLY
    const next = roleCodeOf(roleId) === 'READONLY'
      ? [roleId]
      : [...selectedRoleIds.filter(id => roleCodeOf(id) !== 'READONLY'), roleId];
    setUserRoles(next, next.length === 1 ? roleId : editData.primary_role_id);
  };

  const renderRolesTab = () => {
    const disabled = !editMode && !createMode;
    return (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('users.view.sections.rolePermissions')}</h3>
        <p className="form-hint">{t('users.view.hints.roles')}</p>
        <table className="roles-select-table">
          <thead>
            <tr>
              <th>{t('users.view.fields.roles')}</th>
              <th>{t('users.view.fields.primaryRole')}</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(role => {
              const checked = selectedRoleIds.includes(role.role_id);
              return (
                <tr key={role.role_id}>
                  <td>
                    <label>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(role.role_id)}
                        disabled={disabled}
                      />{' '}
                      {role.role_name}
                    </label>
                  </td>
                  <td>
                    <input
                      type="radio"
                      name="primary_role"
                      aria-label={`${t('users.view.fields.primaryRole')}: ${role.role_name}`}
                      checked={checked && editData.primary_role_id === role.role_id}
                      onChange={() => setUserRoles(selectedRoleIds, role.role_id)}
                      disabled={disabled || !checked}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="form-section">
        <h3>{t('users.view.sections.rolePermissionsDetail')}</h3>
        <div className="permissions-info">
          {selectedRoleCodes.filter(code => t(`users.view.roleDescriptions.${code}`, { defaultValue: '' })).map(code => (
            <div key={code}>{t(`users.view.roleDescriptions.${code}`)}</div>
          ))}
          {selectedRoleCodes.length === 0 && (
            <div>{t('users.view.roleDescriptions.READONLY')}</div>
          )}
        </div>
      </div>
    </div>
    );
  };

  const renderSecurityTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('users.view.sections.accountStatus')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('users.view.fields.status')}</label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editData.is_active || false}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              {t('users.filters.status.active')}
            </label>
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.lastLogin')}</label>
            <input
              type="text"
              value={user?.last_login ? formatAppDate(user.last_login, 'datetime') : t('users.lastLogin.never')}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('users.view.sections.accountInfo')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('users.view.fields.userId')}</label>
            <input
              type="text"
              value={user?.user_id || user?.id || 'N/A'}
              disabled
              readOnly
            />
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.dateCreated')}</label>
            <input
              type="text"
              value={user?.created_date ? formatAppDate(user.created_date, 'datetime') : 'N/A'}
              disabled
              readOnly
            />
          </div>
          <div className="form-group">
            <label>{t('users.view.fields.lastUpdated')}</label>
            <input
              type="text"
              value={user?.updated_date ? formatAppDate(user.updated_date, 'datetime') : 'N/A'}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );

  const handleAddUserSkillClick = () => {
    if (!selectedSkill) {
      showWarning(t('users.view.placeholders.selectSkill'));
      return;
    }
    handleAddUserSkill(parseInt(selectedSkill), selectedProficiency, selectedCertDate);
    setSelectedSkill('');
    setSelectedProficiency('BASIC');
    setSelectedCertDate(localToday());
  };

  const renderSkillsTab = () => {
    const proficiencyColors = {
      BASIC: '#93c5fd',
      INTERMEDIATE: '#a5f3fc',
      ADVANCED: '#86efac',
      EXPERT: '#fde047'
    };

    const isExpired = (expiryDate) => {
      if (!expiryDate) return false;
      return new Date(expiryDate) < new Date();
    };

    return (
      <div className="tab-content">
        <div className="form-section">
          <h3>{t('users.view.sections.userSkills')}</h3>

          {(editMode || createMode) && !createMode && (
            <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px' }}>{t('users.view.skills.addNewSkill')}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '12px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>{t('users.view.skills.skill')}</label>
                  <select
                    value={selectedSkill}
                    onChange={(e) => setSelectedSkill(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                  >
                    <option value="" disabled hidden>{t('users.view.placeholders.selectSkill')}</option>
                    {availableSkills
                      .filter(skill => !userSkills.find(us => us.skill_id === skill.skill_id))
                      .map(skill => (
                        <option key={skill.skill_id} value={skill.skill_id}>
                          {skill.skill_name} ({skill.skill_category})
                        </option>
                      ))
                    }
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>{t('users.view.skills.proficiency')}</label>
                  <select
                    value={selectedProficiency}
                    onChange={(e) => setSelectedProficiency(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                  >
                    <option value="BASIC">{t('users.view.skills.proficiencyLevels.basic')}</option>
                    <option value="INTERMEDIATE">{t('users.view.skills.proficiencyLevels.intermediate')}</option>
                    <option value="ADVANCED">{t('users.view.skills.proficiencyLevels.advanced')}</option>
                    <option value="EXPERT">{t('users.view.skills.proficiencyLevels.expert')}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>{t('users.view.skills.certificationDate')}</label>
                  <input
                    type="date"
                    value={selectedCertDate}
                    onChange={(e) => setSelectedCertDate(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                  />
                </div>
                <button
                  onClick={handleAddUserSkillClick}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--accent-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {t('users.view.skills.addNewSkill')}
                </button>
              </div>
            </div>
          )}

          {userSkills.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎓</div>
              <div>{t('users.view.skills.noSkills')}</div>
              {(editMode || createMode) && <div style={{ fontSize: '14px', marginTop: '8px' }}>{t('users.view.skills.addSkillsHint')}</div>}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
              {userSkills.map(us => (
                <div
                  key={us.user_skill_id}
                  style={{
                    padding: '16px',
                    border: isExpired(us.expiry_date) ? '2px solid #ef4444' : '1px solid var(--border-color)',
                    borderRadius: '6px',
                    background: 'var(--bg-secondary)',
                    position: 'relative'
                  }}
                >
                  {isExpired(us.expiry_date) && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#ef4444',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      EXPIRED
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', paddingRight: isExpired(us.expiry_date) ? '70px' : '0' }}>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>{us.skill_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                        {us.skill_code}
                      </div>
                    </div>
                    {(editMode || createMode) && (
                      <button
                        onClick={() => handleRemoveUserSkill(us.user_skill_id)}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {tCommon('ui.remove')}
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        background: proficiencyColors[us.proficiency_level],
                        color: '#000'
                      }}
                    >
                      {t(`users.view.skills.proficiencyLevels.${us.proficiency_level.toLowerCase()}`)}
                    </span>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-secondary)'
                      }}
                    >
                      {us.skill_category}
                    </span>
                  </div>
                  {us.certification_date && (
                    <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <div>{t('users.view.skills.certified')}: {formatAppDate(us.certification_date)}</div>
                      {us.certified_by_name && (
                        <div>{t('users.view.skills.certifiedBy')}: {us.certified_by_name}</div>
                      )}
                      {us.expiry_date && (
                        <div style={{ color: isExpired(us.expiry_date) ? '#ef4444' : 'inherit' }}>
                          {t('users.view.skills.expires')}: {formatAppDate(us.expiry_date)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };


  if (loading) {
    return <div className="view-container"><div className="loading">{t('users.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!user && !createMode) {
    return <div className="view-container"><div className="no-data">{t('users.view.titles.noUserFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {createMode ? t('users.view.titles.newUser') : `${editData.first_name || ''} ${editData.last_name || ''}`.trim() || editData.username || t('users.filters.roles.user')}
          </h1>
          {!createMode && (
            <span className={`status-badge ${editData.is_active ? 'status-active' : 'status-inactive'}`}>
              {editData.is_active ? t('users.filters.status.active') : t('users.filters.status.inactive')}
            </span>
          )}
          {!createMode && editData.role && (
            <span className={`role-badge role-${editData.role?.toLowerCase()}`}>
              {editData.role}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('users.toolbar.edit')}</button>
              <button className="btn-danger" onClick={handleDelete}>{t('users.toolbar.delete')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('users.toolbar.cancel')}</button>
              <button className="btn-primary" onClick={handleSave} disabled={!hasChanges && !createMode}>
                {createMode ? t('users.toolbar.create') : t('users.toolbar.save')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('users.view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            {t('users.view.tabs.rolesPermissions')}
          </button>
          <button
            className={`tab-item ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            {t('users.view.tabs.security')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'skills' ? 'active' : ''}`}
              onClick={() => setActiveTab('skills')}
            >
              {t('users.view.tabs.skills')}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'roles' && renderRolesTab()}
          {activeTab === 'security' && renderSecurityTab()}
          {activeTab === 'skills' && renderSkillsTab()}
        </div>
      </div>
    </div>
  );
});

ViewUser.displayName = 'ViewUser';

export default ViewUser;